// ─────────────────────────────────────────────
//  Button — token-driven, WCAG 2.2 AA primitive (D-05, D-11)
//  Variants: primary / secondary / danger. Sizes: md / sm / full.
//  Visible >=3:1 focus-visible ring; never outline:none (D-11).
// ─────────────────────────────────────────────
import React, { useState } from "react";
import tokens from "../theme/tokens";

const { color, space, radius, font } = tokens;

export default function Button({
  variant = "primary",
  size = "md",
  disabled = false,
  loading = false,
  onClick,
  children,
  type = "button",
  ariaLabel,
}) {
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  const [focus, setFocus] = useState(false);

  const isDisabled = disabled || loading;
  const v = VARIANTS[variant] || VARIANTS.primary;

  const base = {
    ...styles.base,
    ...SIZES[size],
    background: v.bg,
    color: v.fg,
    border: v.border ? "1px solid " + v.borderColor : "1px solid transparent",
  };

  // State layering — hover/active darken, disabled mutes (a11y: keep visible).
  const stateStyle = isDisabled
    ? styles.disabled
    : active
    ? { background: v.activeBg }
    : hover
    ? { background: v.hoverBg }
    : null;

  // Focus-visible ring (>=3:1 contrast); danger uses the error accent.
  const focusRing = focus
    ? {
        outline: "2px solid " + (variant === "danger" ? color.accent.error : color.accent.primary),
        outlineOffset: "2px",
      }
    : null;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      aria-disabled={isDisabled ? "true" : undefined}
      aria-busy={loading ? "true" : undefined}
      aria-label={ariaLabel}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      style={{ ...base, ...stateStyle, ...focusRing }}
    >
      {/* While loading, the ellipsis is DECORATION and the label stays in the
          accessibility tree. Rendering only "…" replaced the button's
          accessible name — so a screen-reader user pressing "Create account"
          heard the control rename itself to "ellipsis" at the exact moment it
          became busy, with aria-busy pointing at a nameless button (WCAG 2.2
          AA 4.1.2). Visually identical; the name simply survives. */}
      {loading ? (
        <>
          <span aria-hidden="true">…</span>
          <span style={styles.srOnly}>{children}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}

const VARIANTS = {
  primary: {
    bg: color.accent.primary,
    fg: "#ffffff",
    hoverBg: "#1e3a8a",
    activeBg: "#172e6b",
  },
  secondary: {
    bg: color.surface.card,
    fg: color.text.primary,
    border: true,
    borderColor: color.border.default,
    hoverBg: color.surface.raised,
    activeBg: "#e6e9f0",
  },
  danger: {
    bg: color.accent.error,
    fg: "#ffffff",
    hoverBg: "#b91c1c",
    activeBg: "#991b1b",
  },
};

const SIZES = {
  md: { padding: "10px 16px", fontSize: font.size.body },
  sm: { padding: "6px 12px", fontSize: font.size.label },
  full: { padding: "12px 16px", fontSize: font.size.body, width: "100%" },
};

const styles = {
  base: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: space.sm,
    borderRadius: radius.md,
    fontWeight: font.weight.bold,
    fontFamily: font.family,
    cursor: "pointer",
    lineHeight: font.lineHeight.normal,
    transition: "background 120ms ease",
  },
  disabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  // Visually hidden, still announced. The clip-rect idiom rather than
  // display:none or visibility:hidden, both of which remove the text from the
  // accessibility tree — which would defeat the entire point.
  srOnly: {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
    border: 0,
  },
};
